import React, { useEffect, useId, useRef, useState } from 'react';

// A small status chip ("Practice", "Due …") whose explanation opens on click or tap, for status that is worth knowing
// but not worth a banner. Closes on Escape or a click elsewhere.
const InfoChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  // Hide the label on phones (the icon stays); the explanation still names it.
  compactOnPhone?: boolean;
  tone?: 'blue' | 'amber';
  children: React.ReactNode;
}> = ({ icon, label, compactOnPhone = false, tone = 'blue', children }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const tones = tone === 'amber'
    ? 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-900'
    : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-900';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        title={label}
        className={`inline-flex min-h-[2.5rem] items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-colors [&_svg]:w-4 [&_svg]:h-4 [&_svg]:shrink-0 ${tones}`}
      >
        {icon}
        <span className={compactOnPhone ? 'hidden sm:inline' : ''}>{label}</span>
        {compactOnPhone && <span className="sr-only sm:hidden">{label}</span>}
      </button>
      {open && (
        <div
          id={panelId}
          role="status"
          className="absolute right-0 top-full z-50 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700 shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default InfoChip;
